import { TextAreaField, TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, CARD } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import type {
  Contention,
  RebuttalBlock,
} from "@/lib/tool-kit/lit-essays/debate-case";

export interface ContentionStrings {
  legend: string;
  taglineLabel: string;
  taglinePlaceholder: string;
  claimLabel: string;
  claimPlaceholder: string;
  warrantLabel: string;
  warrantPlaceholder: string;
  evidenceLabel: string;
  evidencePlaceholder: string;
  evidenceHint: string;
  impactLabel: string;
  impactPlaceholder: string;
  remove: string;
  removeLabel: string;
}

export interface RebuttalStrings {
  legend: string;
  theySayLabel: string;
  theySayPlaceholder: string;
  weRespondLabel: string;
  weRespondPlaceholder: string;
  becauseLabel: string;
  becausePlaceholder: string;
  impactLabel: string;
  impactPlaceholder: string;
  remove: string;
  removeLabel: string;
}

/** One contention: tagline, claim, warrant, evidence, impact. */
export function ContentionFields({
  contention,
  number,
  strings,
  removable,
  onChange,
  onRemove,
}: {
  contention: Contention;
  /** The formatted ordinal shown to the reader. */
  number: string;
  strings: ContentionStrings;
  removable: boolean;
  onChange: (change: Partial<Contention>) => void;
  onRemove: () => void;
}) {
  const id = contention.id;
  return (
    <fieldset className={`${CARD} space-y-4`}>
      <legend className="px-1 font-medium text-foreground text-sm">
        {t(strings.legend, { n: number })}
      </legend>
      <TextField
        id={`dc-tagline-${id}`}
        label={strings.taglineLabel}
        onChange={(tagline) => onChange({ tagline })}
        placeholder={strings.taglinePlaceholder}
        value={contention.tagline}
      />
      <TextAreaField
        id={`dc-claim-${id}`}
        label={strings.claimLabel}
        onChange={(claim) => onChange({ claim })}
        placeholder={strings.claimPlaceholder}
        prose
        rows={2}
        value={contention.claim}
      />
      <TextAreaField
        id={`dc-warrant-${id}`}
        label={strings.warrantLabel}
        onChange={(warrant) => onChange({ warrant })}
        placeholder={strings.warrantPlaceholder}
        prose
        rows={2}
        value={contention.warrant}
      />
      <TextAreaField
        hint={strings.evidenceHint}
        id={`dc-evidence-${id}`}
        label={strings.evidenceLabel}
        onChange={(evidence) => onChange({ evidence })}
        placeholder={strings.evidencePlaceholder}
        prose
        rows={3}
        value={contention.evidence}
      />
      <TextAreaField
        id={`dc-impact-${id}`}
        label={strings.impactLabel}
        onChange={(impact) => onChange({ impact })}
        placeholder={strings.impactPlaceholder}
        prose
        rows={2}
        value={contention.impact}
      />
      {removable ? (
        <button
          aria-label={t(strings.removeLabel, { n: number })}
          className={BUTTON_SMALL}
          onClick={onRemove}
          type="button"
        >
          {strings.remove}
        </button>
      ) : null}
    </fieldset>
  );
}

/** One prepared rebuttal block: they say, we respond, because, therefore. */
export function RebuttalFields({
  block,
  number,
  strings,
  onChange,
  onRemove,
}: {
  block: RebuttalBlock;
  number: string;
  strings: RebuttalStrings;
  onChange: (change: Partial<RebuttalBlock>) => void;
  onRemove: () => void;
}) {
  const id = block.id;
  return (
    <fieldset className={`${CARD} space-y-4`}>
      <legend className="px-1 font-medium text-foreground text-sm">
        {t(strings.legend, { n: number })}
      </legend>
      <TextAreaField
        id={`dr-say-${id}`}
        label={strings.theySayLabel}
        onChange={(theySay) => onChange({ theySay })}
        placeholder={strings.theySayPlaceholder}
        prose
        rows={2}
        value={block.theySay}
      />
      <TextAreaField
        id={`dr-respond-${id}`}
        label={strings.weRespondLabel}
        onChange={(weRespond) => onChange({ weRespond })}
        placeholder={strings.weRespondPlaceholder}
        prose
        rows={2}
        value={block.weRespond}
      />
      <TextAreaField
        id={`dr-because-${id}`}
        label={strings.becauseLabel}
        onChange={(because) => onChange({ because })}
        placeholder={strings.becausePlaceholder}
        prose
        rows={2}
        value={block.because}
      />
      <TextAreaField
        id={`dr-impact-${id}`}
        label={strings.impactLabel}
        onChange={(impact) => onChange({ impact })}
        placeholder={strings.impactPlaceholder}
        prose
        rows={2}
        value={block.impact}
      />
      <button
        aria-label={t(strings.removeLabel, { n: number })}
        className={BUTTON_SMALL}
        onClick={onRemove}
        type="button"
      >
        {strings.remove}
      </button>
    </fieldset>
  );
}
